import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, X, Loader2, CheckCircle2, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { LGAS, DISCIPLINES, OTHERS_LGA } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MembershipCard from "@/components/MembershipCard";

export default function Register() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", lga: "", discipline: "", expertise: "", bio: "" });
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [member, setMember] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const selectedDiscipline = DISCIPLINES.find((d) => d.category === form.discipline);

  useEffect(() => () => stopCam(), []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "discipline" ? { expertise: "" } : {}) }));

  const uploadBlob = async (blob, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
      setPhotoPreview(URL.createObjectURL(blob));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadBlob(f, f.name);
  };

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch {
      toast.error("Could not access camera. Please upload a photo instead.");
    }
  };

  const stopCam = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => { uploadBlob(blob, "capture.png"); stopCam(); }, "image/png");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photoPath) return toast.error("Please add a photo");
    if (!form.lga || !form.discipline || !form.expertise) return toast.error("Please complete all required fields");
    setSubmitting(true);
    try {
      const { data } = await api.post("/members", { ...form, photo_path: photoPath });
      setMember(data);
      toast.success("Registration successful! Your card is ready.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCard = () => {
    window.print();
  };

  if (member) {
    return (
      <main className="pt-28 pb-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-9 h-9 text-emerald-400" />
          </motion.div>
          <div className="overline-label mb-3">Welcome to the movement</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">You're officially a member!</h1>
          <p className="text-zinc-400 mt-3">Here is your digital membership card. Scan the QR code to verify your membership anytime.</p>
          <div className="mt-10 print-card"><MembershipCard member={member} /></div>
          <div className="mt-8 flex justify-center gap-3">
            <Button onClick={downloadCard} data-testid="membership-card-download-button" className="bg-[#E6197E] hover:bg-[#C4136B] rounded-full px-6">
              <Download className="w-4 h-4 mr-2" /> Download / Print Card
            </Button>
            <Button variant="outline" className="border-white/15 bg-transparent rounded-full px-6" onClick={() => { setMember(null); setForm({ full_name: "", phone: "", email: "", lga: "", discipline: "", expertise: "", bio: "" }); setPhotoPath(null); setPhotoPreview(null); }}>
              <RotateCcw className="w-4 h-4 mr-2" /> Register Another
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-20 min-h-screen">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <div className="overline-label mb-3">Member Registration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Join Nasarawa Expo</h1>
        <p className="text-zinc-400 mt-3">Fill in your details, add a photo and select your creative field. You'll get a digital membership card instantly.</p>

        <form onSubmit={submit} data-testid="registration-form" className="mt-10 space-y-6">
          {/* Photo */}
          <div className="rounded-2xl border border-white/10 bg-[#131519] p-6">
            <Label className="text-sm font-semibold">Profile Photo *</Label>
            <div className="mt-4 flex flex-col sm:flex-row items-center gap-5">
              <div className="w-28 h-28 rounded-xl bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center border border-white/10">
                {photoPreview ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-zinc-600" />}
              </div>
              {camOn ? (
                <div className="flex-1 w-full">
                  <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg max-h-48 object-cover bg-black" />
                  <div className="flex gap-2 mt-3">
                    <Button type="button" size="sm" onClick={capture} className="bg-[#E6197E] hover:bg-[#C4136B]">Capture</Button>
                    <Button type="button" size="sm" variant="outline" className="border-white/15 bg-transparent" onClick={stopCam}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="registration-photo-input" />
                  <Button type="button" variant="outline" className="border-white/15 bg-transparent" disabled={uploading} onClick={() => fileRef.current?.click()}>
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload Photo
                  </Button>
                  <Button type="button" variant="outline" className="border-white/15 bg-transparent" onClick={startCam} data-testid="registration-webcam-button">
                    <Camera className="w-4 h-4 mr-2" /> Take Photo
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Full Name *</Label><Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-2 bg-[#131519] border-white/10" data-testid="registration-name-input" /></div>
            <div><Label>Phone *</Label><Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 bg-[#131519] border-white/10" data-testid="registration-phone-input" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 bg-[#131519] border-white/10" data-testid="registration-email-input" /></div>

          <div>
            <Label>Local Government Area *</Label>
            <Select value={form.lga} onValueChange={(v) => set("lga", v)}>
              <SelectTrigger className="mt-2 bg-[#131519] border-white/10" data-testid="registration-lga-select"><SelectValue placeholder="Select your LGA" /></SelectTrigger>
              <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}<SelectItem value={OTHERS_LGA}>{OTHERS_LGA}</SelectItem></SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Creative Discipline *</Label>
              <Select value={form.discipline} onValueChange={(v) => set("discipline", v)}>
                <SelectTrigger className="mt-2 bg-[#131519] border-white/10" data-testid="registration-discipline-select"><SelectValue placeholder="Select discipline" /></SelectTrigger>
                <SelectContent>
                  {DISCIPLINES.map((d) => <SelectItem key={d.category} value={d.category}>{d.category}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedDiscipline && <p className="mt-2 text-xs text-zinc-400 leading-relaxed" data-testid="registration-discipline-desc">{selectedDiscipline.desc}</p>}
            </div>
            <div>
              <Label>Specific Expertise *</Label>
              <Select value={form.expertise} onValueChange={(v) => set("expertise", v)} disabled={!selectedDiscipline}>
                <SelectTrigger className="mt-2 bg-[#131519] border-white/10" data-testid="registration-expertise-select"><SelectValue placeholder="Select expertise" /></SelectTrigger>
                <SelectContent>{selectedDiscipline?.subcategories.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {!selectedDiscipline && <p className="mt-2 text-xs text-zinc-500">Choose a discipline first to see expertise options.</p>}
            </div>
          </div>

          <div><Label>Short Bio</Label><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Tell clients about your work..." className="mt-2 bg-[#131519] border-white/10" data-testid="registration-bio-input" /></div>

          <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#E6197E] hover:bg-[#C4136B] text-base font-semibold rounded-full" data-testid="registration-submit-button">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating your card...</> : "Complete Registration"}
          </Button>
        </form>
      </div>
    </main>
  );
}
